// Run progress for the video overview's panel, from GET /videos/{id}/status.
//
// A run takes one of three routes (backend/app/pipeline/runner.py): the GPU picker's
// single 'vision' stage, the CPU stages it replaces when the service is off or fails, or
// -- for a photo set (kind 'photos'), which has no video to probe or sample at an interval
// (Anshul, 2026-09-26) -- its own 'photos' stage reading the uploaded images straight into
// the same describe/recurrence/verify/cap/match/assess pipeline every other route feeds.
// RunStatusResponse.stage is free text (schemas/videos.py), so an unrecognised stage still
// renders as its raw name.

export const CPU_STAGES = [
  'probing',
  'sampling',
  'filtering',
  'dedup',
  'grouping',
  'describing',
  'recurrence',
  'verifying',
  'capping',
  'matching',
  'assessing',
];

export const VISION_STAGES = [
  'probing',
  'vision',
  'describing',
  'recurrence',
  'verifying',
  'capping',
  'matching',
  'assessing',
];

// A photo-set run records exactly these stages (this phase's backend facts) and NEVER
// probing/sampling/filtering/dedup/grouping -- those would never complete on this route,
// which would permanently undercount its progress by their weight (the same bug
// 'recurrence' being missing used to cause on CPU_STAGES, see the test file's own note on
// that) and, worse, park the label on "Filtering frames" for the entire describing stage
// since the "next stage after the last completed one" lookup would walk straight past
// 'photos' into the uncompletable 'filtering'/'dedup'/'grouping' entries a real run skips.
export const PHOTO_STAGES = [
  'photos',
  'describing',
  'recurrence',
  'verifying',
  'capping',
  'matching',
  'assessing',
];

const STAGE_LABELS = {
  probing: 'Probing video',
  vision: 'Picking frames (GPU, every frame)',
  sampling: 'Sampling frames',
  photos: 'Reading photos',
  filtering: 'Filtering frames',
  dedup: 'De-duplicating',
  grouping: 'Grouping observations',
  describing: 'Describing & mapping',
  recurrence: 'Merging recurring findings',
  verifying: 'Verifying findings',
  capping: 'Capping findings',
  matching: 'Matching answer key',
  // RUNLOG V8: the last stage writes the rows of GRIHA's report from the whole run.
  assessing: 'Writing the GRIHA report',
};

// Both routes that replaced the old CPU flow -- the GPU picker (vision) and a photo set
// (photos, this phase's backend facts: same stage list as vision with 'probing'+'vision'
// folded into one 'photos' stage) -- keep every finding, no D13 cap, so their 'capping'
// stage only ranks them (runner._stage_cap); the plain CPU route is the only one that
// still drops findings there.
const NO_CAP_ROUTE_LABELS = { capping: 'Ranking findings' };

// The GPU job's own sub-stage (RunStatusResponse.counts.vision_stage, new): named here so
// the panel has something better to say than a bar stuck at 0% while the machine boots,
// well before there is any frame count to turn into a percentage. 'extracting' is listed
// for when vision_progress_pct is not yet reported for it; once it is, the percentage
// branch below takes over instead of this fixed sentence.
const VISION_SUBSTAGE_LABELS = {
  acquiring_gpu: 'Getting a GPU machine',
  booting: 'Starting the GPU machine',
  downloading: 'Reading the video on the GPU',
  extracting: 'Picking frames (GPU): extracting frames',
  selecting: 'Picking frames (GPU): choosing frames',
  writing: 'Picking frames (GPU): saving frames',
  uploading: 'Picking frames (GPU): uploading frames',
};

// Rough, strictly-increasing position within the vision stage for each sub-stage that has
// no percentage of its own -- enough to make the bar visibly move while booting, not a
// measured fraction of wall-clock time.
const VISION_SUBSTAGE_FRACTIONS = {
  acquiring_gpu: 0.02,
  booting: 0.05,
  downloading: 0.08,
  extracting: 0.5,
  selecting: 0.95,
  writing: 0.97,
  uploading: 0.99,
};

export function stageLabel(stage, route = 'cpu') {
  if (!stage) return 'Not started';
  if (route !== 'cpu' && NO_CAP_ROUTE_LABELS[stage]) return NO_CAP_ROUTE_LABELS[stage];
  return STAGE_LABELS[stage] || stage;
}

// `stage` on the status is the last stage FINISHED. While a run is processing, the panel shows
// the one being worked on instead: the GPU step alone can take minutes, and "Probing video"
// for all of that reads as a stuck run. Right after probing the route is not yet known (the
// backend tries the GPU and may fall back), so that step gets a label true of both.
//
// While the GPU job runs, the backend publishes counts.vision_progress_pct (frames extracted over
// the video's frame count, 100 once it is choosing frames): on a 90-150 minute video that stage
// alone takes hours, so the label and the bar move within it.
//
// A finished run says so. `stage` is still the last stage it finished, and on every route
// that is 'assessing', whose label "Writing the GRIHA report" read as work still under way
// on a run marked done (QA 2026-09-29).
//
// `kind` is the video's own kind (VideosPage/VideoOverviewPage's `video.kind`), passed by
// the caller so a photo set is recognised as such from the very first poll -- `status.stage`
// only ever holds the last FINISHED stage, so it is never `'photos'` while that stage is
// still running, and guessing the route from it alone left the first poll of a photo run
// reading "Probing video" instead of "Reading photos".
export function runProgress(status, kind) {
  const state = status?.state || 'idle';
  const completed = Array.isArray(status?.stages_completed) ? status.stages_completed : [];
  const counts = status?.counts || {};
  const last = completed[completed.length - 1];
  const visionRunning =
    state === 'processing' &&
    last === 'probing' &&
    counts.vision_fallback !== 1 &&
    (typeof counts.vision_progress_pct === 'number' || typeof counts.vision_stage === 'string');
  const isPhotoRoute = kind === 'photos' || completed.includes('photos');
  const route = isPhotoRoute ? 'photos' : completed.includes('vision') || visionRunning ? 'vision' : 'cpu';
  const order = route === 'photos' ? PHOTO_STAGES : route === 'vision' ? VISION_STAGES : CPU_STAGES;

  let label = state === 'done' ? 'Complete' : stageLabel(status?.stage, route);
  let partial = 0;
  if (state === 'processing') {
    if (completed.length === 0) label = stageLabel(route === 'photos' ? 'photos' : 'probing');
    else if (visionRunning) {
      const stageKey = counts.vision_stage;
      if (typeof counts.vision_progress_pct === 'number' && (!stageKey || stageKey === 'extracting')) {
        const gpuPct = counts.vision_progress_pct;
        label =
          gpuPct >= 100 ? 'Picking frames (GPU): choosing frames' : `Picking frames (GPU): ${gpuPct}%`;
        partial = Math.min(1, Math.max(0, gpuPct / 100));
      } else if (stageKey && VISION_SUBSTAGE_LABELS[stageKey]) {
        label = VISION_SUBSTAGE_LABELS[stageKey];
        partial = VISION_SUBSTAGE_FRACTIONS[stageKey];
      } else {
        label = 'Picking frames';
      }
    } else if (last === 'probing') label = 'Picking frames';
    else {
      const next = order[order.indexOf(last) + 1];
      label = next ? stageLabel(next, route) : stageLabel(last, route);
    }
  }

  const pct =
    state === 'done'
      ? 100
      : Math.min(100, Math.round(((completed.length + partial) / order.length) * 100));
  return { state, completed, route, label, pct };
}
